import { usePWA } from "@/hooks/usePWA";
import { useConnectivity } from "@/contexts/ConnectivityContext";
import { connectivity } from "@/services/connectivity";
import { useLang } from "@/contexts/LangContext";
import { Download, RefreshCw, WifiOff } from "lucide-react";
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";

const DISMISS_KEY = "pwa-banner-dismissed-at";
const DISMISS_DAYS = 30;

export function isInstallBannerDismissed() {
  try {
    const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    return ts > 0 && Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch { return false; }
}

export function resetInstallBannerDismissal() {
  try { localStorage.removeItem(DISMISS_KEY); } catch { /* noop */ }
}

// Routes where the install banner must never appear (POS work area, public pages, auth)
const SUPPRESSED_PREFIXES = ["/pos", "/login", "/setup", "/shop", "/track", "/platform", "/signup"];

// True when any modal dialog is currently open (checkout, returns, imports, receipt preview, ...)
function useDialogOpen(enabled) {
  const [dialogOpen, setDialogOpen] = useState(false);
  useEffect(() => {
    if (!enabled) return undefined;
    const check = () => setDialogOpen(
      !!document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]')
    );
    check();
    const obs = new MutationObserver(check);
    obs.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["data-state"] });
    return () => obs.disconnect();
  }, [enabled]);
  return enabled && dialogOpen;
}

export function PWAInstallBanner() {
  const { canInstall, promptInstall } = usePWA();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(isInstallBannerDismissed);

  const suppressedRoute = SUPPRESSED_PREFIXES.some(p => location.pathname === p || location.pathname.startsWith(`${p}/`));
  const active = canInstall && !dismissed && !suppressedRoute;
  const dialogOpen = useDialogOpen(active);

  if (!active || dialogOpen) return null;

  const handleDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
    setDismissed(true);
  };

  const handleInstall = async () => {
    const accepted = await promptInstall();
    if (!accepted) handleDismiss();
  };

  return (
    <div role="region" aria-label="Install app banner"
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-sm z-40 animate-in slide-in-from-bottom-4 duration-300"
      data-testid="pwa-install-banner">
      <div className="bg-cream-900 text-white rounded-2xl p-4 shadow-2xl border border-cream-700/30">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
            <Download className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm">Install AR POS</p>
            <p className="text-xs text-white/60 mt-0.5">Add to home screen for quick access and offline support</p>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dismiss install banner for 30 days"
            className="flex-1 h-9 rounded-xl text-xs font-medium text-white/60 hover:text-white/80 hover:bg-white/5 focus:outline-none focus:ring-2 focus:ring-white/40 transition-colors"
            data-testid="pwa-dismiss-btn"
          >
            Not now
          </button>
          <button
            type="button"
            onClick={handleInstall}
            aria-label="Install app"
            className="flex-1 h-9 rounded-xl bg-[#E08A4E] text-white text-xs font-semibold hover:bg-[#c97a42] focus:outline-none focus:ring-2 focus:ring-white/40 transition-colors flex items-center justify-center gap-1.5"
            data-testid="pwa-install-btn"
          >
            <Download className="w-3.5 h-3.5" />
            Install App
          </button>
        </div>
      </div>
    </div>
  );
}

export function PWAUpdateBanner() {
  const { updateAvailable, applyUpdate } = usePWA({ watchVersion: true });
  if (!updateAvailable) return null;

  return (
    <div className="fixed top-4 left-4 right-4 md:left-auto md:right-6 md:top-6 md:max-w-sm z-50 animate-in slide-in-from-top-4 duration-300" data-testid="pwa-update-banner">
      <div className="bg-cream-900 text-white rounded-2xl p-4 shadow-2xl">
        <div className="flex items-center gap-3">
          <RefreshCw className="w-5 h-5 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-semibold text-sm">Update Available</p>
            <p className="text-xs text-white/70 mt-0.5">A new version is ready — reload to avoid errors</p>
          </div>
          <button onClick={applyUpdate} className="h-8 px-4 rounded-xl bg-cream-50 text-clay-500 text-xs font-semibold hover:bg-cream-100 transition-colors" data-testid="pwa-update-btn">
            Update
          </button>
        </div>
      </div>
    </div>
  );
}

export function OfflineIndicator() {
  const { online } = useConnectivity();
  const { t } = useLang();
  const [show, setShow] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);
  const [details, setDetails] = useState(false);

  useEffect(() => {
    if (!online) {
      setShow(true);
      setWasOffline(true);
    } else if (wasOffline) {
      // Show "Back online" briefly, then hide
      const t1 = setTimeout(() => { setShow(false); setWasOffline(false); setDetails(false); }, 3000);
      return () => clearTimeout(t1);
    } else {
      setShow(false);
    }
  }, [online, wasOffline]);

  if (!show) return null;
  const log = details ? connectivity.getLog().slice(0, 12) : [];

  return (
    <div className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 ${online ? "bg-green-500" : "bg-amber-500"}`} data-testid="offline-indicator">
      <div className="flex items-center justify-center gap-2 py-1.5 px-4">
        {online ? (
          <p className="text-xs font-medium text-white">{t("net_back_online")}</p>
        ) : (
          <>
            <WifiOff className="w-3.5 h-3.5 text-white" />
            <p className="text-xs font-medium text-white">{t("net_reconnecting")}</p>
            <span className="hidden sm:inline text-[10px] text-white/85" data-testid="offline-reassure">{t("net_offline_hint")}</span>
            <button onClick={() => setDetails(d => !d)} className="text-[10px] underline text-white/90" data-testid="offline-details-btn">{t("net_details")}</button>
          </>
        )}
      </div>
      {details && !online && (
        <div className="bg-amber-600/95 px-4 py-2 max-h-44 overflow-y-auto" data-testid="offline-details-panel">
          <p className="text-[10px] text-white/90 mb-1">{t("net_details_hint")}</p>
          {log.length === 0 ? (
            <p className="text-[10px] text-white/80">—</p>
          ) : log.map((e, i) => (
            <p key={i} className="text-[10px] font-mono text-white/90">
              {new Date(e.at).toLocaleTimeString()} · {e.type === "down" ? "⛔ connection lost" : e.type === "up" ? "✅ reconnected" : "⚠ check failed"} {e.detail && `(${e.detail})`}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
